import React, { useEffect, useMemo, useState } from 'react';

/**
 * Page publique « Valider mon CV » — erpia.ch/valider-cv/:jeton
 *
 * Le recruteur a préparé le CV du candidat au format entreprise pour un poste
 * et y a ajouté des précisions. Le candidat, sans compte et souvent depuis
 * son téléphone, répond d'abord aux QUESTIONS du recruteur (avec ses mots, ou
 * « je ne l'ai pas fait » — chaque réponse est facultative ; Leman écrit
 * ensuite ses réponses dans le CV), puis relit le CV : les AJOUTS sont
 * surlignés (l'élément signature de la page), et pour chacun il Valide,
 * Corrige ou Refuse. Il peut commenter n'importe quelle ligne.
 *
 * Ce que la page ne montre jamais, parce que le serveur ne l'envoie pas : le
 * score, le tarif, le nom du client.
 *
 * Routes SANS auth :
 *   GET  /public/cv-validation/:jeton → { prenom, poste, expire_le, deja_repondu, questions:[{ id, question }],
 *        cv:{ nom, titre, profil, sections:[{ titre, lignes:[{ id, texte, ajout_id }] }] }, ajouts:[{ id, texte, mission }] }
 *        404 inconnu · 410 expiré ou révoqué
 *   POST /public/cv-validation/:jeton { questions:[{ id, reponse, pas_fait }], reponses:[{ ajout_id, decision, texte }],
 *        commentaires:[{ ligne, texte }], site }
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const dateCourte = (d) => { const x = d ? new Date(d) : null; return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' }) : ''; };

function Message({ icone, titre, texte }) {
  return (
    <main className="pubform">
      <div className="pubform-carte pubform-carte--message">
        <BI icon={icone} />
        <h1>{titre}</h1>
        {texte && <p>{texte}</p>}
      </div>
    </main>
  );
}

export default function PublicValiderCv({ jeton }) {
  const [etat, setEtat] = useState('chargement'); // chargement | pret | invalide | expire | envoye | deja
  const [data, setData] = useState(null);
  const [reponses, setReponses] = useState({});        // ajout_id → { decision, texte }
  const [questions, setQuestions] = useState({});      // id de question → { reponse, pas_fait }
  const [commentaires, setCommentaires] = useState({}); // ligne → texte
  const [ouverts, setOuverts] = useState({});          // ligne → bool (champ de commentaire visible)
  const [site, setSite] = useState('');               // piège à robots
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/public/cv-validation/${encodeURIComponent(jeton)}`);
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 410) { setEtat('expire'); return; }
        if (!r.ok || j.success === false) { setEtat('invalide'); return; }
        setData(j);
        document.title = 'Votre CV — à relire';
        setEtat(j.deja_repondu ? 'deja' : 'pret');
      } catch { if (!annule) setEtat('invalide'); }
    })();
    return () => { annule = true; };
  }, [jeton]);

  const ajouts = useMemo(() => new Map((data?.ajouts || []).map(a => [String(a.id), a])), [data]);
  const total = ajouts.size;
  const decides = [...ajouts.keys()].filter(id => reponses[id]?.decision && (reponses[id].decision !== 'corrige' || String(reponses[id].texte || '').trim())).length;

  const listeQuestions = Array.isArray(data?.questions) ? data.questions : [];
  const repondues = listeQuestions.filter(q => questions[q.id]?.pas_fait || String(questions[q.id]?.reponse || '').trim()).length;
  const poserQuestion = (id, patch) => setQuestions(s => ({ ...s, [id]: { ...(s[id] || { reponse: '', pas_fait: false }), ...patch } }));

  const decider = (id, decision) => setReponses(s => ({ ...s, [id]: { decision, texte: decision === 'corrige' ? (s[id]?.texte ?? ajouts.get(id)?.texte ?? '') : '' } }));

  const envoyer = async (e) => {
    e.preventDefault();
    setErreur('');
    if (decides < total) { setErreur(`Il reste ${total - decides} ajout${total - decides > 1 ? 's' : ''} à valider, corriger ou refuser.`); return; }
    setBusy(true);
    try {
      const corps = {
        questions: listeQuestions
          .map(q => ({ id: q.id, ...(questions[q.id] || {}) }))
          .filter(q => q.pas_fait || String(q.reponse || '').trim())
          .map(q => (q.pas_fait ? { id: q.id, pas_fait: true } : { id: q.id, reponse: String(q.reponse).trim() })),
        reponses: [...ajouts.keys()].map(id => ({ ajout_id: Number(id) || id, decision: reponses[id].decision, texte: reponses[id].decision === 'corrige' ? String(reponses[id].texte || '').trim() : undefined })),
        commentaires: Object.entries(commentaires).filter(([, t]) => String(t || '').trim()).map(([ligne, texte]) => ({ ligne, texte: String(texte).trim() })),
        site,
      };
      const r = await fetch(`${BASE}/public/cv-validation/${encodeURIComponent(jeton)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 410) { setEtat('expire'); return; }
      if (r.status === 404) { setEtat('invalide'); return; }
      if (r.status === 409) { setEtat('deja'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || 'Vos réponses n’ont pas pu être envoyées. Réessayez dans un instant.');
      setEtat('envoye');
      window.scrollTo?.(0, 0);
    } catch (err) { setErreur(err.message); }
    finally { setBusy(false); }
  };

  if (etat === 'chargement') return <Message icone="arrow-repeat" titre="Chargement de votre CV…" />;
  if (etat === 'invalide') return <Message icone="link-45deg" titre="Ce lien n’est plus valide" texte="Demandez-en un nouveau à votre recruteur." />;
  if (etat === 'expire') return <Message icone="clock-history" titre="Ce lien a expiré" texte="Ce lien n’est plus valide. Demandez-en un nouveau à votre recruteur." />;
  if (etat === 'deja' || etat === 'envoye') return <Message icone="check2-circle" titre="Merci" texte="Vos réponses ont bien été transmises à votre recruteur." />;

  const cv = data.cv || {};
  // Une fonction, pas un composant : un composant déclaré ici serait recréé à
  // chaque frappe et ferait perdre le curseur dans les champs.
  const ligne = (l) => {
    const id = l.ajout_id != null ? String(l.ajout_id) : null;
    const rep = id ? reponses[id] : null;
    return (
      <li key={l.id} className={`pubcv-ligne${id ? ' pubcv-ligne--ajout' : ''}${rep?.decision === 'refuse' ? ' is-refusee' : ''}`}>
        <div className="pubcv-ligne__texte">
          {id && <span className="pubcv-ligne__marque">Ajout proposé</span>}
          <span>{l.texte}</span>
        </div>
        {id && (
          <>
            <div className="pubcv-choix" role="group" aria-label="Votre décision sur cet ajout">
              <button type="button" aria-pressed={rep?.decision === 'valide'} onClick={() => decider(id, 'valide')}><BI icon="check2" /> Valider</button>
              <button type="button" aria-pressed={rep?.decision === 'corrige'} onClick={() => decider(id, 'corrige')}><BI icon="pencil" /> Corriger</button>
              <button type="button" aria-pressed={rep?.decision === 'refuse'} onClick={() => decider(id, 'refuse')}><BI icon="x-lg" /> Refuser</button>
            </div>
            {rep?.decision === 'corrige' && (
              <label className="pubform-champ pubcv-correction">
                <span className="pubform-label">Votre version</span>
                <textarea rows={3} value={rep.texte} onChange={e => setReponses(s => ({ ...s, [id]: { ...rep, texte: e.target.value } }))} />
              </label>
            )}
          </>
        )}
        {ouverts[l.id] ? (
          <label className="pubform-champ pubcv-commentaire">
            <span className="pubform-label">Votre commentaire <em>facultatif</em></span>
            <textarea rows={2} value={commentaires[l.id] || ''} onChange={e => setCommentaires(s => ({ ...s, [l.id]: e.target.value }))} />
          </label>
        ) : (
          <button type="button" className="pubcv-commenter" onClick={() => setOuverts(s => ({ ...s, [l.id]: true }))}><BI icon="chat-left-text" /> Commenter</button>
        )}
      </li>
    );
  };

  return (
    <main className="pubform">
      <form className="pubform-carte pubcv" onSubmit={envoyer} noValidate>
        <header className="pubform-tete">
          <p className="pubform-kicker">Votre CV, à relire</p>
          <h1>{data.prenom ? `Bonjour ${data.prenom},` : 'Bonjour,'}</h1>
          <p>
            Votre recruteur a préparé votre CV pour le poste <strong>{data.poste || 'proposé'}</strong>.
            {listeQuestions.length > 0 && ' Il a d’abord quelques questions sur votre parcours : vos réponses compléteront votre CV.'}
            {total > 0 && <> Il y a aussi ajouté quelques précisions, <span className="pubcv-surligne">surlignées</span> : validez, corrigez ou refusez chacune. Elles n’entreront dans votre CV qu’avec votre accord.</>}
            {' '}Vous pouvez commenter n’importe quelle ligne.
          </p>
          {data.expire_le && <p className="pubcv-expire"><BI icon="clock" /> Lien valable jusqu’au {dateCourte(data.expire_le)}.</p>}
        </header>

        {erreur && <div className="pubform-erreur" role="alert">{erreur}</div>}

        {listeQuestions.length > 0 && (
          <section className="pubcv-questions" aria-labelledby="pubcv-questions-titre">
            <h2 id="pubcv-questions-titre">Quelques questions sur votre parcours</h2>
            <p>Répondez avec vos mots : dans quelle mission, combien de temps, avec quel résultat. Chaque réponse est facultative.</p>
            {listeQuestions.map((q, i) => {
              const v = questions[q.id] || { reponse: '', pas_fait: false };
              return (
                <div key={q.id} className={`pubcv-question${v.pas_fait ? ' is-pasfait' : ''}`}>
                  <label className="pubform-champ" htmlFor={`pubcv-q-${i}`}>
                    <span className="pubform-label">{q.question}</span>
                    <textarea id={`pubcv-q-${i}`} rows={3} maxLength={1000} value={v.reponse} disabled={v.pas_fait}
                      onChange={e => poserQuestion(q.id, { reponse: e.target.value })} />
                  </label>
                  <label className="pubcv-pasfait">
                    <input type="checkbox" checked={v.pas_fait} onChange={e => poserQuestion(q.id, { pas_fait: e.target.checked })} />
                    Je ne l’ai pas fait
                  </label>
                </div>
              );
            })}
          </section>
        )}

        <div className="pubcv-cv">
          {(cv.nom || cv.titre) && (
            <div className="pubcv-identite">
              {cv.nom && <h2>{cv.nom}</h2>}
              {cv.titre && <p>{cv.titre}</p>}
            </div>
          )}
          {cv.profil && <p className="pubcv-profil">{cv.profil}</p>}
          {(cv.sections || []).map((s, i) => (
            <section key={i} className="pubcv-section">
              {s.titre && <h3>{s.titre}</h3>}
              <ul>{(s.lignes || []).map(ligne)}</ul>
            </section>
          ))}
        </div>

        {/* Piège à robots : invisible pour une personne. */}
        <label className="pubcv-piege" aria-hidden="true">Site web<input tabIndex={-1} autoComplete="off" value={site} onChange={e => setSite(e.target.value)} /></label>

        <div className="pubform-pied">
          <span className="pubform-reste">
            {[
              listeQuestions.length ? `${repondues}/${listeQuestions.length} question${listeQuestions.length > 1 ? 's' : ''}` : null,
              total ? `${decides}/${total} ajout${total > 1 ? 's' : ''} traité${decides > 1 ? 's' : ''}` : null,
            ].filter(Boolean).join(' · ') || 'Aucun ajout à valider — vos commentaires sont les bienvenus.'}
          </span>
          <button type="submit" className="pubform-envoyer" disabled={busy}>{busy ? 'Envoi…' : 'Envoyer mes réponses'}</button>
        </div>
      </form>
    </main>
  );
}
